import { useState } from 'react'
import type { JSX } from 'react'
import { Link } from 'react-router'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useQuery } from '@/api/query'
import { useBrand } from '@/brand/context'
import { SpendPanel } from '@/components/SpendPanel'
import { formatDate, formatDateIn, greeting } from '@/lib/format'
import { useActiveTenant, useTenants } from '@/tenant/context'
import { Empty, ErrorState, Loading } from '@/ui/states'
import { KeywordRankChart, KeywordRankTable } from './RankChart'
import { hasKeywordLines } from './rank-series'

type SearchDashboard = Schema<'SearchDashboardOut'>
type ContentDashboard = Schema<'ContentDashboardOut'>
type VitalsDashboard = Schema<'VitalsDashboardOut'>
type TrafficDashboard = Schema<'TrafficDashboardOut'>
type ImpactDashboard = Schema<'ImpactDashboardOut'>
type AttributedPage = Schema<'AttributedPageOut'>
type VitalsMeasurement = Schema<'VitalsMeasurementOut'>
type VitalsReading = Schema<'VitalsReadingOut'>
type Tile = Schema<'TileOut'>
type Point = Schema<'PointOut'>

/** The ranges `app/seo/dashboard.RANGES` offers, and the only ones it accepts. */
const RANGES = [7, 28, 90] as const
type Range = (typeof RANGES)[number]

/**
 * The SEO dashboard — S-D5's screen 1, on real data (S-B32a).
 *
 * **Ranking leads because ranking is the promise**, which is the design's own
 * note. What sits below it is S-B32b's (Core Web Vitals, budget burn) and
 * S-B32c's (the reasoning trail); this file owns the four headline tiles and the
 * rank-movement chart.
 *
 * **Two queries, not one, and they fail apart.** The Search Console half is three
 * live calls to Google; the content half is two counts against our own schema.
 * A Google outage must not blank *"12 published, 3 awaiting you"* — a figure
 * AugRank knows perfectly well, about work the tenant paid for, and the one tile
 * that leads somewhere they can act. `app/v1/seo_dashboard.py` argues the split.
 *
 * **The badge's colour comes from `direction`, never from the sign of `delta`.**
 * A falling average position is an improvement and a falling click count is not,
 * so the server applies *lower is better* and this renders the verdict rather
 * than re-deriving it. Re-deriving it in TypeScript is precisely how the best
 * month a tenant has had gets a red arrow on it.
 *
 * **An absent comparison renders as no badge at all.** `comparable` is false for
 * a property connected less than two windows ago, and the alternative — Google's
 * zeroes passed through — would read *+100%* on every tile, permanently, for the
 * tenants least able to judge whether any of this works.
 */
export function SeoDashboard(): JSX.Element {
  const brand = useBrand()
  const [siteId, setSiteId] = useState<string | null>(null)
  const [days, setDays] = useState<Range>(28)
  // P-MOB-02. Whether the phone layout has opened the full dashboard.
  const [expanded, setExpanded] = useState(false)

  const sites = useQuery((signal) => call(() => api.GET('/api/v1/seo/sites', { signal })), [])

  // The first site until somebody chooses another. Derived rather than set from
  // an effect, for `SearchPerformance`'s reason: an effect would render once
  // with no selection and once with one, and refetch on the second.
  const listed = sites.state.data?.sites ?? []
  const selectedId = siteId ?? listed[0]?.id ?? null
  const site = listed.find((row) => row.id === selectedId) ?? null
  const hasProperty = site !== null && site.gsc_property !== null && site.gsc_property !== ''

  const search = useQuery(
    (signal) =>
      selectedId === null || !hasProperty
        ? Promise.resolve({ ok: true as const, data: null })
        : call(() =>
            api.GET('/api/v1/seo/sites/{site_id}/dashboard/search', {
              params: { path: { site_id: selectedId }, query: { days } },
              signal,
            }),
          ),
    [selectedId, hasProperty, days],
  )

  const content = useQuery(
    (signal) =>
      selectedId === null
        ? Promise.resolve({ ok: true as const, data: null })
        : call(() =>
            api.GET('/api/v1/seo/sites/{site_id}/dashboard/content', {
              params: { path: { site_id: selectedId }, query: { days } },
              signal,
            }),
          ),
    [selectedId, days],
  )

  return (
    <div className="ar-stack ar-stack--loose">
      {selectedId !== null ? (
        <MobileDigest
          search={search}
          content={content}
          hasProperty={hasProperty}
          days={days}
          expanded={expanded}
          onToggle={() => {
            setExpanded((current) => !current)
          }}
        />
      ) : null}
      <div
        className="ar-stack ar-stack--loose app-dash-rest"
        data-collapsed={selectedId !== null && !expanded ? '' : undefined}
      >
        <div className="ar-row" style={{ justifyContent: 'space-between' }}>
          <h1 className="ar-h1">SEO</h1>
          <div className="ar-btn-group">
            {/* Always drawn, one site or several (parity P-SDASH-01). */}
            {listed.length > 0 ? (
              <select
                className="ar-select"
                style={{ height: 'var(--ar-control-sm)', width: 'auto' }}
                aria-label="Site"
                value={selectedId ?? ''}
                onChange={(event) => {
                  setSiteId(event.target.value)
                }}
              >
                {listed.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.domain}
                  </option>
                ))}
              </select>
            ) : null}
            <div className="ar-btn-group" role="group" aria-label="Period">
              {RANGES.map((range) => (
                <button
                  key={range}
                  type="button"
                  className={`ar-btn ar-btn--sm ${days === range ? 'ar-btn--primary' : 'ar-btn--ghost'}`}
                  // `aria-pressed` rather than `aria-current`: toggle buttons over
                  // one value, not links to three places.
                  aria-pressed={days === range}
                  onClick={() => {
                    setDays(range)
                  }}
                >
                  Last {range} days
                </button>
              ))}
            </div>
          </div>
        </div>

        {sites.state.status === 'loading' ? <Loading label="Loading your sites" lines={3} /> : null}

        {sites.state.status === 'error' ? (
          <div className="ar-card">
            <ErrorState
              failure={sites.state.failure}
              title="Could not load your sites"
              onRetry={sites.refetch}
            />
          </div>
        ) : null}

        {sites.state.status === 'ready' && listed.length === 0 ? (
          <div className="ar-card">
            <Empty
              icon="🌐"
              title="Add a website to get started"
              body={`Point ${brand.productName} at a site and it will audit it, track what it ranks for, and show the movement here.`}
              action={
                <Link className="ar-btn ar-btn--primary" to="/onboarding">
                  Add a site
                </Link>
              }
            />
          </div>
        ) : null}

        {selectedId !== null ? (
          <Tiles search={search} content={content} hasProperty={hasProperty} days={days} />
        ) : null}

        {/* What AugRank did — the value statement, organisation-wide, over the
          same window as the tiles. First after the tiles because it is the
          answer to the question a paying tenant opens this screen with. */}
        {selectedId !== null ? <Activity days={days} /> : null}

        {selectedId !== null && hasProperty ? <RankMovement search={search} /> : null}

        {selectedId !== null ? (
          <div className="ar-grid ar-grid--2">
            <CoreWebVitals siteId={selectedId} days={days} />
            {/* SEO-UI-23. Framed, so this grid cell is one card whose top edge
              lines up with Core Web Vitals beside it. */}
            <SpendPanel module="seo" framed />
          </div>
        ) : null}

        {/* **Last on the screen, and that is the design's own note rather than a
          layout preference**: `design/screens/seo.html`'s *why this shape* says
          revenue sits at the bottom because it needs GA4 and most tenants will
          not have it on day one — putting it at the top would make the dashboard
          look broken to exactly the tenant least able to tell. */}
        {selectedId !== null ? <TrafficAndRevenue siteId={selectedId} days={days} /> : null}

        {selectedId !== null ? <ContentImpact siteId={selectedId} days={days} /> : null}
      </div>
    </div>
  )
}

/**
 * The dashboard as `shell.html#mobile` draws it below 1100px (parity P-MOB-02,
 * 2026-09-13): a greeting, one *Average position* card with its movement
 * badge, and one card saying how many drafts are waiting with a full-width
 * *Review drafts* button. Nothing else — the mockup's note is that approving
 * on a phone is the one thing a tenant opens it for.
 *
 * **Everything else is one press away, not gone.** The owner's rule for the
 * parity fixes is that no capability is cut, so the desktop dashboard sits
 * under a *Show everything* disclosure rather than behind a breakpoint. The
 * digest itself is `display: none` above 1100px (`.app-dash-digest`), where
 * the full dashboard is the screen.
 *
 * Reads the same two queries as `Tiles`, so it costs no request of its own.
 * A site with no Search Console property gets no position card; the drafts
 * card is real either way, because it is AugRank's own count.
 */
function MobileDigest({
  search,
  content,
  hasProperty,
  days,
  expanded,
  onToggle,
}: {
  search: SearchQuery
  content: ContentQuery
  hasProperty: boolean
  days: Range
  expanded: boolean
  onToggle: () => void
}): JSX.Element {
  // `useTenants`, not `useActiveTenant`: the digest mounts as soon as the site
  // list answers, which in a test that renders the dashboard outside the
  // tenant guard can be before the organisation has. Asia/Kolkata is the
  // product's zone (CLAUDE.md) and is only ever the greeting's fallback.
  const timeZone = useTenants().active?.tenant.timezone ?? 'Asia/Kolkata'
  const figures = hasProperty && search.state.status === 'ready' ? search.state.data : null
  const position = figures?.has_data ? figures.position : null
  const awaiting = content.state.status === 'ready' ? content.state.data?.awaiting_approval : null
  const loading = search.state.status === 'loading' || content.state.status === 'loading'

  return (
    <div className="ar-stack app-dash-digest">
      <h1 className="ar-h2">{greeting(new Date(), timeZone)}</h1>
      {loading ? (
        <Loading label="Loading your figures" lines={2} />
      ) : (
        <>
          {/* No card at all without a measurement — the exit criterion `Tiles`
              honours: a tile reading *—* or *0* beside *Average position*
              asserts a rank that does not exist. The desktop half says how to
              connect Search Console; the digest just leaves the space. */}
          {position !== null && position.current !== null ? (
            <div className="ar-card">
              <div className="ar-card__body">
                <div className="ar-muted" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
                  Average position
                </div>
                <div className="ar-h1 ar-num">{position.current.toLocaleString('en-IN')}</div>
                {figures?.comparable ? <DigestBadge moved={position} days={days} /> : null}
              </div>
            </div>
          ) : null}
          <div className="ar-card">
            <div className="ar-card__body">
              {awaiting !== null && awaiting !== undefined && awaiting > 0 ? (
                <>
                  <strong>
                    {awaiting.toLocaleString('en-IN')}{' '}
                    {awaiting === 1 ? 'draft needs' : 'drafts need'} you
                  </strong>
                  <p
                    className="ar-muted"
                    style={{
                      margin: 'var(--ar-space-2) 0 var(--ar-space-4)',
                      fontSize: 'var(--ar-text-body-sm-size)',
                    }}
                  >
                    Approving on your phone works — the diff is readable at this width.
                  </p>
                  <Link className="ar-btn ar-btn--primary ar-btn--block" to="/seo/approvals">
                    Review drafts
                  </Link>
                </>
              ) : (
                <>
                  <strong>No drafts need you</strong>
                  <p
                    className="ar-muted"
                    style={{
                      margin: 'var(--ar-space-2) 0 var(--ar-space-4)',
                      fontSize: 'var(--ar-text-body-sm-size)',
                    }}
                  >
                    New drafts wait here for your approval before anything is published.
                  </p>
                  <Link className="ar-btn ar-btn--ghost ar-btn--block" to="/seo/approvals">
                    Open the queue
                  </Link>
                </>
              )}
            </div>
          </div>
        </>
      )}
      <button
        type="button"
        className="ar-btn ar-btn--ghost ar-btn--block"
        aria-expanded={expanded}
        onClick={onToggle}
      >
        {expanded ? 'Show less' : 'Show everything'}
      </button>
    </div>
  )
}

/**
 * The digest's movement badge — `shell.html:250` draws *"▲ 4.2 this month"*:
 * the raw delta in places (SEO-UI-22) and the window in words. *This month*
 * is the 28-day default; the other two windows name their days.
 */
function DigestBadge({ moved, days }: { moved: Tile; days: Range }): JSX.Element | null {
  if (moved.direction === 'unknown' || moved.delta === null) return null
  const tone =
    moved.direction === 'better'
      ? 'ar-badge--success'
      : moved.direction === 'worse'
        ? 'ar-badge--danger'
        : 'ar-badge--neutral'
  const arrow = moved.direction === 'better' ? '▲' : '▼'
  const window = days === 28 ? 'this month' : `in ${String(days)} days`
  return (
    <div className={`ar-badge ${tone}`} style={{ marginTop: 'var(--ar-space-2)' }}>
      {moved.direction === 'flat'
        ? `Unchanged ${window}`
        : `${arrow} ${Math.abs(moved.delta).toFixed(1)} ${window}`}
    </div>
  )
}

/**
 * Traffic and revenue — the money end of the loop (S-B33).
 *
 * **Its own query, and a 200 when nothing is connected.** A tenant who has not
 * chosen an Analytics property has not made a mistake, so `connected: false`
 * draws a prompt rather than an error state — S-B33's exit criterion is that the
 * dashboard *works* with this panel absent, and an error state on a screen where
 * nothing is wrong is not that.
 *
 * **The window is the search window, not GA4's own.** Analytics lags a day where
 * Search Console lags three, and the panel above and the table below both cover
 * the longer one — a screen whose figures can be added together is worth two
 * days of freshness. `app/v1/seo_dashboard.py` argues it at the route.
 *
 * **Revenue is a tile only when the property records money.** Most SEO tenants
 * are lead generation and will never send a purchase event, and a permanent
 * ₹0.00 reads as *the traffic earned nothing* rather than as *this property does
 * not measure money*. The server decides — `revenue` is null — because the same
 * judgement made twice is the one that eventually disagrees.
 */
function TrafficAndRevenue({ siteId, days }: { siteId: string; days: Range }): JSX.Element {
  const traffic = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/seo/sites/{site_id}/dashboard/traffic', {
          params: { path: { site_id: siteId }, query: { days } },
          signal,
        }),
      ),
    [siteId, days],
  )

  return (
    <div className="ar-card">
      <div className="ar-card__header">
        <h2>Traffic and revenue</h2>
        <span className="ar-card__spacer" />
        {traffic.state.status === 'ready' && traffic.state.data.connected ? (
          <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
            From Google Analytics
          </span>
        ) : null}
        {/* The design's own badge. It says *why* the card below is empty in two
            words, at the point a reader's eye is already on the heading. */}
        {traffic.state.status === 'ready' && !traffic.state.data.connected ? (
          <span className="ar-badge ar-badge--neutral">Needs GA4</span>
        ) : null}
      </div>
      <div className="ar-card__body">
        {traffic.state.status === 'loading' ? (
          <Loading label="Loading your traffic" lines={3} />
        ) : null}
        {traffic.state.status === 'error' ? (
          <ErrorState
            failure={traffic.state.failure}
            title="Could not load your Analytics figures"
            onRetry={traffic.refetch}
          />
        ) : null}
        {traffic.state.status === 'ready' ? (
          <Traffic data={traffic.state.data} days={days} />
        ) : null}
      </div>
    </div>
  )
}

type ActivityData = Schema<'SeoActivityOut'>

/**
 * *What AugRank did* over the window — findings resolved, fixes applied,
 * drafts, pages live, the keywords that moved, what outreach did — read from
 * `/seo/dashboard/activity`, which is the Monday summary's own counts on
 * demand. A quiet window says so and says what would change it.
 */
function Activity({ days }: { days: Range }): JSX.Element {
  const activity = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/seo/dashboard/activity', { params: { query: { days } }, signal }),
      ),
    [days],
  )
  return (
    <section className="ar-card" aria-label="What AugRank did">
      <div className="ar-card__header">
        <h2>What AugRank did</h2>
        <span className="ar-card__spacer" />
        <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
          Last {String(days)} days
        </span>
      </div>
      <div className="ar-card__body">
        {activity.state.status === 'loading' ? (
          <Loading label="Adding up what was done" lines={2} />
        ) : null}
        {activity.state.status === 'error' ? (
          <ErrorState
            failure={activity.state.failure}
            title="Could not add up what was done"
            onRetry={activity.refetch}
          />
        ) : null}
        {activity.state.status === 'ready' ? <ActivityBody data={activity.state.data} /> : null}
      </div>
    </section>
  )
}

function ActivityBody({ data }: { data: ActivityData }): JSX.Element {
  if (data.quiet) {
    return (
      <Empty
        title="Nothing yet in this window"
        body={
          data.keywords_tracked === 0
            ? 'Track a few keywords and connect Search Console: the audit, the rank check and the drafts all start from those.'
            : 'The audit found nothing new, no keyword moved and no draft was written. A scheduled pass under Run schedule is what fills this in.'
        }
      />
    )
  }
  const stats: { label: string; value: number; to?: string }[] = [
    { label: 'Issues resolved', value: data.findings_resolved, to: '/seo/audit' },
    { label: 'Fixes applied to your pages', value: data.fixes_applied, to: '/seo/approvals' },
    { label: 'Drafts written', value: data.drafts_written, to: '/seo/content' },
    { label: 'Pages published', value: data.published, to: '/seo/publish-targets' },
    { label: 'Waiting for you', value: data.awaiting_approval, to: '/seo/approvals' },
    { label: 'Leads found', value: data.leads_found, to: '/outreach/leads' },
    { label: 'Messages sent', value: data.messages_sent, to: '/outreach/inbox' },
    { label: 'Replies', value: data.replies, to: '/outreach/inbox' },
  ]
  const gains = data.gains ?? []
  const losses = data.losses ?? []
  return (
    <div className="ar-stack">
      <div className="app-stats">
        {stats
          .filter((stat) => stat.value > 0)
          .map((stat) => (
            <div key={stat.label} className="app-stat">
              <span className="app-stat__label">{stat.label}</span>
              <span className="app-stat__value">
                {stat.to ? (
                  <Link className="ar-link" to={stat.to}>
                    {stat.value.toLocaleString('en-IN')}
                  </Link>
                ) : (
                  stat.value.toLocaleString('en-IN')
                )}
              </span>
            </div>
          ))}
      </div>
      {gains.length > 0 || losses.length > 0 ? (
        <p className="ar-text-body-sm" style={{ margin: 0 }}>
          <strong>Rankings:</strong>{' '}
          {[...gains, ...losses]
            .map((move) =>
              move.places > 0
                ? `“${move.keyword}” up ${String(move.places)} to ${String(move.to_position)}`
                : `“${move.keyword}” down ${String(Math.abs(move.places))} to ${String(move.to_position)}`,
            )
            .join('; ')}
          .
        </p>
      ) : data.keywords_tracked > 0 ? (
        <p className="ar-subtle ar-text-body-sm" style={{ margin: 0 }}>
          None of your {String(data.keywords_tracked)} tracked keywords moved by a whole place.
        </p>
      ) : null}
      {data.uptime_failures > 0 ? (
        <p className="ar-text-body-sm" style={{ margin: 0 }}>
          <strong>Uptime:</strong> {String(data.uptime_failures)}{' '}
          {data.uptime_failures === 1 ? 'check' : 'checks'} failed —{' '}
          <Link className="ar-link" to="/seo/sites">
            see when
          </Link>
          .
        </p>
      ) : null}
    </div>
  )
}

/** The panel's body: two empty states that say different things, or the tiles. */
function Traffic({ data, days }: { data: TrafficDashboard; days: Range }): JSX.Element {
  const brand = useBrand()
  if (!data.connected) {
    // SEO-UI-4, SEO-UI-25. **Two prompts, because there are two situations.**
    // This always sent the tenant to `/seo/search-performance` — the property
    // picker — and for a tenant who has connected no Google account at all that
    // is a screen which can only tell them to go and connect one. The design's
    // CTA is *"Connect Google Analytics"*, which is the settings panel that owns
    // the grant; the picker is right only once a grant exists. `google_connected`
    // is the field that separates them. Both land on Connections since the
    // settings consolidation's second batch (2026-09-15): the picker is on the
    // Analytics card there, under the grant it rides on.
    return data.google_connected ? (
      <Empty
        icon="₹"
        title="Choose the Analytics property for this site"
        body={`Your Google account is connected. Point ${brand.productName} at the Analytics property this site reports to and sessions, conversions and revenue appear here, beside the search figures.`}
        action={
          <Link
            className="ar-btn ar-btn--primary ar-btn--sm"
            to="/settings/connections#analytics-properties"
          >
            Choose a property
          </Link>
        }
      />
    ) : (
      <Empty
        icon="₹"
        title="Connect Google Analytics to see what SEO is actually earning"
        body="Sessions, conversions and revenue come from Google Analytics. Connect the Google account that owns this site's Analytics property, and the figures appear here beside the search ones."
        action={
          <Link className="ar-btn ar-btn--primary ar-btn--sm" to="/settings/connections">
            Connect Google Analytics
          </Link>
        }
      />
    )
  }

  if (!data.has_data) {
    return (
      <Empty
        icon="📈"
        title="Google Analytics has no data for this property yet"
        body={`A property created recently, or one whose tag is not on the site, genuinely has nothing to report. Figures for the last ${String(days)} days will appear here once Google has them.`}
      />
    )
  }

  return (
    <div className="ar-stack">
      <div className={`ar-grid ${data.revenue === null ? 'ar-grid--3' : 'ar-grid--4'}`}>
        <Figure label="Sessions" moved={data.sessions} comparable={data.comparable} />
        <Figure label="Users" moved={data.total_users} comparable={data.comparable} />
        {/* *Conversions* rather than *key events*: Google renamed the metric and
            the API only answers to the new name, but the word on the screen is
            the one the tenant uses about their own business. */}
        <Figure label="Conversions" moved={data.key_events} comparable={data.comparable} />
        {data.revenue !== null && data.revenue_display !== null ? (
          <div className="ar-card">
            <div className="ar-card__body">
              <div className="ar-muted" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
                Revenue
              </div>
              {/* Formatted on the server, in the **property's** currency — which
                  is the tenant's customers' money and not the currency AugRank
                  bills in. A second implementation here would disagree about
                  exactly one value: the lakh. */}
              <div className="ar-h1 ar-num">{data.revenue_display}</div>
              {data.comparable ? <Badge moved={data.revenue} /> : null}
            </div>
          </div>
        ) : null}
      </div>
      {data.revenue === null ? (
        <p className="ar-muted" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
          This property records no revenue. Analytics reports money only when ecommerce or purchase
          events are set up — conversions above are the events it does count.
        </p>
      ) : null}
    </div>
  )
}

/**
 * What Search Console says about one article's URL.
 *
 * **A page Google has never shown is said so in words**, not rendered as
 * *0 · 0 · —*. It is the ordinary state of an article published this week and it
 * is the step of the sequence the tenant is waiting on — indexed, then ranked,
 * then visited — so a row of zeroes would read as a page Google looked at and
 * ignored.
 */
function InSearch({ page }: { page: AttributedPage }): JSX.Element {
  if (page.impressions === null || page.clicks === null) return <span className="ar-subtle">—</span>
  if (page.impressions === 0) {
    return <span className="ar-subtle">Not shown yet</span>
  }
  return (
    <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
      {page.clicks.toLocaleString('en-IN')} clicks · {page.impressions.toLocaleString('en-IN')}{' '}
      shown
      {page.position === null ? '' : ` · position ${page.position.toFixed(1)}`}
    </span>
  )
}

/**
 * One Analytics tile.
 *
 * Deliberately not `Tile`: that one renders an em dash for a null `current`,
 * which is the *average position* case — rank 0 does not exist. Every figure
 * here is a count or an amount, where a zero is a real measurement and an em
 * dash would hide it.
 */
function Figure({
  label,
  moved,
  comparable,
}: {
  label: string
  moved: Tile | null
  comparable: boolean
}): JSX.Element | null {
  if (moved === null) return null
  return (
    <div className="ar-card">
      <div className="ar-card__body">
        <div className="ar-muted" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
          {label}
        </div>
        <div className="ar-h1 ar-num">
          {moved.current === null ? '0' : moved.current.toLocaleString('en-IN')}
        </div>
        {comparable ? <Badge moved={moved} /> : null}
      </div>
    </div>
  )
}

/**
 * What each published article earned (S-B33).
 *
 * **This is attribution, not causation, and the caption says so.** Three figures
 * AugRank observed in three independent systems — it published on the 12th,
 * Google began showing the URL on the 15th, Analytics recorded the sessions and
 * the purchases — with no modelled contribution anywhere. A seasonal peak and a
 * newsletter produce the same three numbers, so the panel states the sequence
 * and leaves the conclusion to the reader.
 *
 * **The rank columns can be absent while the money is not.** Search Console and
 * Analytics fail independently, and losing the explanation of how a visitor
 * arrived must not lose the record that they came — `search_available` says
 * which happened rather than rendering *never shown*.
 */
function ContentImpact({ siteId, days }: { siteId: string; days: Range }): JSX.Element {
  const impact = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/seo/sites/{site_id}/dashboard/impact', {
          params: { path: { site_id: siteId }, query: { days } },
          signal,
        }),
      ),
    [siteId, days],
  )

  return (
    <div className="ar-card">
      <div className="ar-card__header">
        <h2>What your content earned</h2>
        <span className="ar-card__spacer" />
        {impact.state.status === 'ready' && impact.state.data.has_data ? (
          <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
            {impact.state.data.total_pages.toLocaleString('en-IN')} live{' '}
            {impact.state.data.total_pages === 1 ? 'article' : 'articles'}
          </span>
        ) : null}
      </div>
      <div className="ar-card__body">
        {impact.state.status === 'loading' ? (
          <Loading label="Matching your articles against Google" lines={4} />
        ) : null}
        {impact.state.status === 'error' ? (
          <ErrorState
            failure={impact.state.failure}
            title="Could not work out what your content earned"
            onRetry={impact.refetch}
          />
        ) : null}
        {impact.state.status === 'ready' ? <Impact data={impact.state.data} days={days} /> : null}
      </div>
    </div>
  )
}

/** The table, or one of three empty states that mean three different things. */
function Impact({ data, days }: { data: ImpactDashboard; days: Range }): JSX.Element {
  const brand = useBrand()
  // The **organisation's** zone, not the browser's. The server decided
  // `published_in_window` from the tenant's own dates, so a browser-zone date
  // could sit beside a *New* badge for a period it falls outside of.
  const timeZone = useActiveTenant().tenant.timezone

  if (!data.connected) {
    return (
      <Empty
        icon="💰"
        title="Connect Google Analytics to see what each article earned"
        body={`${brand.productName} knows what it published and when. Analytics knows which page a visit started on and what it was worth. Connect it and the two are matched up here, article by article.`}
        action={
          <Link
            className="ar-btn ar-btn--primary ar-btn--sm"
            to="/settings/connections#analytics-properties"
          >
            Choose a property
          </Link>
        }
      />
    )
  }

  if (data.pages.length === 0) {
    return (
      <Empty
        icon="✍️"
        title="Publish an article and its traffic will show up here"
        body={`This table matches everything ${brand.productName} has published against what Google Analytics and Search Console say about those pages. It fills in once there is something live to match.`}
        action={
          <Link className="ar-btn ar-btn--primary ar-btn--sm" to="/seo/approvals">
            Go to the queue
          </Link>
        }
      />
    )
  }

  if (!data.has_data) {
    return (
      <Empty
        icon="⏳"
        title="Your live articles have no traffic in this period yet"
        body={`Google takes time to index and rank a new page, and the last ${String(days)} days may be before that happened. The ${String(data.total_pages)} live ${data.total_pages === 1 ? 'article is' : 'articles are'} being matched — there is nothing to credit them with yet.`}
      />
    )
  }

  return (
    <div className="ar-stack">
      {/* **Wrapped, and this is S-DFIX2's one-line defect rather than a habit.**
          `.ar-table-wrap`'s `overflow-x: auto` is what lets a five-column table
          scroll inside its own card at 390px instead of pushing the whole page
          sideways — the defect that hit eleven tenant screens at once, and the
          widest table this dashboard has. */}
      <div className="ar-table-wrap" tabIndex={0}>
        <table className="ar-table">
          <caption className="ar-muted">
            What each live article was credited with over the period. These are three separate
            measurements lined up in order — {brand.productName} published it, Search Console shows
            it, Analytics counted the visit — not a calculation of what the article caused. Sessions
            count arrivals from everywhere; the search clicks beside them count only the ones Google
            sent, so a wide gap between the two is traffic that found the page some other way.
          </caption>
          <thead>
            <tr>
              <th scope="col">Article</th>
              <th scope="col">Sessions</th>
              <th scope="col">Conversions</th>
              {data.has_revenue ? <th scope="col">Revenue</th> : null}
              {/* The three Search Console figures in one column rather than three.
                  They are one row of one report and they are read together — how
                  often Google showed the page, how often that turned into a visit,
                  and where it sat — and three columns of a five-column table would
                  push the money off the side of a laptop. */}
              {data.search_available ? <th scope="col">In search</th> : null}
            </tr>
          </thead>
          <tbody>
            {data.pages.map((page) => (
              <tr key={page.content_id}>
                <td>
                  <a className="ar-link" href={page.url} target="_blank" rel="noreferrer noopener">
                    {page.title === '' ? page.path : page.title}
                  </a>
                  {page.published_in_window ? (
                    <span
                      className="ar-badge ar-badge--success"
                      style={{ marginInlineStart: 'var(--ar-space-2)' }}
                    >
                      New
                    </span>
                  ) : null}
                  {/* The first event in the sequence, on the row that states it.
                      Everything to the right of this column happened after it. */}
                  <div className="ar-subtle" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
                    Published {formatDateIn(page.published_at, timeZone)}
                  </div>
                </td>
                <td className="ar-num">{page.sessions.toLocaleString('en-IN')}</td>
                <td className="ar-num">{page.key_events.toLocaleString('en-IN')}</td>
                {data.has_revenue ? <td className="ar-num">{page.revenue_display}</td> : null}
                {data.search_available ? (
                  <td>
                    <InSearch page={page} />
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="ar-muted" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
        {/* *Of which*, never a bare attributed figure presented as the site's
            total: direct traffic, the home page and every page AugRank did not
            write are in the site figure and in none of the rows above. */}
        {data.sessions.toLocaleString('en-IN')} of the site&rsquo;s{' '}
        {data.site_sessions.toLocaleString('en-IN')} sessions
        {data.session_share === null ? '' : ` (${data.session_share.toFixed(1)}%)`} started on an
        article {brand.productName} published, and they account for{' '}
        {data.key_events.toLocaleString('en-IN')} of its{' '}
        {data.site_key_events.toLocaleString('en-IN')} conversions
        {data.has_revenue
          ? ` and ${data.revenue_display} of its ${data.site_revenue_display}${
              data.revenue_share === null ? '' : ` (${data.revenue_share.toFixed(1)}%)`
            }.`
          : '.'}
        {data.total_pages > data.pages.length
          ? ` Showing the top ${String(data.pages.length)} of ${String(data.total_pages)}.`
          : ''}
      </p>

      {!data.search_available ? (
        <p className="ar-muted" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
          Search Console did not answer, so the impressions and position columns are not shown. The
          traffic figures above are unaffected — they come from Analytics.
        </p>
      ) : null}

      {data.truncated ? (
        <p className="ar-muted" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
          This property has more pages than Analytics returns in one report, so its longest tail was
          not read. It cannot affect an article that had traffic — Google returns the busiest pages
          first.
        </p>
      ) : null}
    </div>
  )
}

/**
 * Core Web Vitals — the card, and the trend behind it (S-B32b).
 *
 * **Its own query.** These come from `site_vitals`, which an audit writes; the
 * tiles above come from Google. A Search Console outage must not blank a
 * measurement AugRank took itself, and an audit that has never run must not
 * blank the tiles. `app/v1/seo_dashboard.py` makes the same split server-side.
 *
 * **The band decides the colour, and the band comes from the server.** Google's
 * thresholds live in `app/seo/pagespeed.THRESHOLDS` and are applied there, so a
 * tenant comparing this card with PageSpeed Insights' own page sees the same
 * three colours. Re-deriving them here from the raw value would be a second copy
 * of Google's numbers that could drift from theirs by a release.
 *
 * **Every figure arrives formatted.** `display` is *"1.9 s"* or *"214 ms"* off
 * the endpoint, because a second implementation of when a millisecond becomes a
 * second would disagree about exactly one value and nobody would find it.
 *
 * **A metric with no number is absent rather than zero.** INP has no lab
 * equivalent, so a site with too little traffic for CrUX has two vitals and not
 * three — which the card says, instead of drawing a third at 0 ms.
 */
function CoreWebVitals({ siteId, days }: { siteId: string; days: Range }): JSX.Element {
  const vitals = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/seo/sites/{site_id}/dashboard/vitals', {
          params: { path: { site_id: siteId }, query: { days } },
          signal,
        }),
      ),
    [siteId, days],
  )

  return (
    <div className="ar-card">
      <div className="ar-card__header">
        <h2>Core Web Vitals</h2>
        <span className="ar-card__spacer" />
        {vitals.state.status === 'ready' && vitals.state.data.needs_work > 0 ? (
          <span className="ar-badge ar-badge--warning">
            ⚠ {vitals.state.data.needs_work} needs work
          </span>
        ) : null}
      </div>
      <div className="ar-card__body">
        {vitals.state.status === 'loading' ? (
          <Loading label="Loading your Core Web Vitals" lines={3} />
        ) : null}
        {vitals.state.status === 'error' ? (
          <ErrorState
            failure={vitals.state.failure}
            title="Could not load your Core Web Vitals"
            onRetry={vitals.refetch}
          />
        ) : null}
        {vitals.state.status === 'ready' ? <Vitals data={vitals.state.data} /> : null}
      </div>
    </div>
  )
}

/**
 * The card's body: two empty states that say different things, or the figures.
 *
 * *Never audited* and *audited but PageSpeed did not answer* are genuinely
 * different situations — one is waiting on the tenant, the other on Google — and
 * a single "no data yet" would send the first tenant looking for a fault and
 * leave the second waiting for a run that already happened.
 *
 * **SEO-UI-26 — one form factor at a time, chosen, not both stacked.** The card
 * rendered a labelled 3-up grid per strategy, so it stood at roughly twice the
 * height of the *Spend against cap* panel beside it in the same `ar-grid--2` and
 * the two cells' bottom edges came apart by a card's worth of space.
 * `seo.html:139-149` draws one 3-up grid. Both measurements are still reachable
 * — a switch in the card, not a second block — and **mobile is the default**
 * because mobile is the measurement Google ranks on.
 *
 * A site with only one strategy measured draws no switch: a two-option control
 * with one option is furniture.
 */
function Vitals({ data }: { data: VitalsDashboard }): JSX.Element {
  const [strategy, setStrategy] = useState<string | null>(null)

  if (!data.has_data) {
    return data.audited ? (
      <Empty
        icon="⏱"
        title="The audit ran, but PageSpeed Insights did not answer"
        body="Core Web Vitals come from Google's PageSpeed Insights, which is rate-limited and occasionally unavailable. Everything else the audit found is on the audit screen; the next run measures again."
        action={
          <Link className="ar-btn ar-btn--ghost ar-btn--sm" to="/seo/audit">
            See the audit
          </Link>
        }
      />
    ) : (
      <Empty
        icon="⏱"
        title="Run an audit to measure this site's speed"
        body="Core Web Vitals — how quickly the page paints, how fast it responds to a tap, and how much it shifts while loading — are measured by the technical audit on both mobile and desktop."
        action={
          <Link className="ar-btn ar-btn--primary ar-btn--sm" to="/seo/audit">
            Go to the audit
          </Link>
        }
      />
    )
  }

  // Mobile first where it was measured, then whatever there is. Derived rather
  // than set from an effect, for the reason the site picker above gives.
  const mobile = data.latest.find((measured) => measured.strategy === 'mobile')
  const fallback = mobile ?? data.latest[0]
  const shown = data.latest.find((measured) => measured.strategy === strategy) ?? fallback
  if (shown === undefined) return <></>

  return (
    <div className="ar-stack">
      {data.latest.length > 1 ? (
        <div className="ar-btn-group" role="group" aria-label="Form factor">
          {data.latest.map((measured) => (
            <button
              key={measured.strategy}
              type="button"
              className={`ar-btn ar-btn--sm ${
                measured.strategy === shown.strategy ? 'ar-btn--primary' : 'ar-btn--ghost'
              }`}
              aria-pressed={measured.strategy === shown.strategy}
              onClick={() => {
                setStrategy(measured.strategy)
              }}
            >
              {measured.strategy === 'mobile' ? 'Mobile' : 'Desktop'}
            </button>
          ))}
        </div>
      ) : null}
      <Measurement measured={shown} labelled={data.latest.length === 1} />
      <Diagnosis measured={shown} />
      <p className="ar-muted" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
        {data.points.length > 1
          ? `Measured ${String(data.points.length)} times in the last ${String(data.days)} days. `
          : ''}
        <Link className="ar-link" to="/seo/audit">
          See the audit
        </Link>
        .
      </p>
    </div>
  )
}

/**
 * SEO-UI-3. Which metric is dragging, said in words, above the link to the
 * screen that can explain it.
 *
 * The design's sentence is *"INP is dragged down by the gallery script on 4
 * pages"*, and the second half of that is not this card's to say: the script and
 * the page count are the audit's per-finding evidence, which is a different
 * payload behind a different route, and duplicating a slice of it here would be
 * a second thing to keep in step with `app/seo/findings.py`. What is genuinely
 * on this card is *which* metric and *how badly* — `band` is the server's
 * verdict against Google's own thresholds — so the sentence names the metric and
 * hands off, which is what the link beside it was already for.
 *
 * Nothing at all when every metric is good: a card whose three figures are green
 * does not need a sentence explaining that they are.
 */
function Diagnosis({ measured }: { measured: VitalsMeasurement }): JSX.Element | null {
  const poor = measured.metrics.filter((reading) => reading.band === 'poor')
  const fair = measured.metrics.filter((reading) => reading.band === 'needs_improvement')
  const worst = [...poor, ...fair]
  if (worst.length === 0) return null

  const names = worst.map((reading) => reading.label)
  const listed =
    names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1] ?? ''}`
  const verb = names.length === 1 ? 'is' : 'are'
  const where = measured.strategy === 'mobile' ? 'on mobile' : 'on desktop'

  // **One phrase for both bands, and it is the accurate one.** This read
  // *"below Google's threshold"* whenever any metric was `poor`, which
  // overstates a `needs_improvement` one sitting beside it: that band is
  // *above* the poor threshold and below the good one, so *below* is the wrong
  // word for it. *Short of* is true of both, and the per-metric badge over each
  // figure already carries which of the two it is — the sentence does not need
  // to encode severity a second time and get it wrong for half the list.
  return (
    <p className="ar-muted" style={{ fontSize: 'var(--ar-text-body-sm-size)', margin: 0 }}>
      <strong>{listed}</strong> {verb} short of Google&rsquo;s threshold {where}. The audit lists
      the pages and what is slowing them down.
    </p>
  )
}

/**
 * One form factor's three figures, with when and how they were measured.
 *
 * `labelled` is false once the switch above is on screen (SEO-UI-26): the
 * control already names the form factor, and a heading repeating it under the
 * pressed button is the same word twice in two lines.
 */
function Measurement({
  measured,
  labelled,
}: {
  measured: VitalsMeasurement
  labelled: boolean
}): JSX.Element {
  return (
    <div>
      <div className="app-cwv__strategy">
        {labelled ? <strong>{measured.strategy === 'mobile' ? 'Mobile' : 'Desktop'}</strong> : null}
        {/* Field and lab are not interchangeable: "your real users see 4.2s" and
            "a simulated phone saw 4.2s" deserve different reactions, so the card
            says which it is rather than presenting the second as the first. */}
        <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
          {measured.field_data ? 'From real Chrome users' : 'From a lab run'} ·{' '}
          {formatDate(measured.measured_at)}
        </span>
      </div>
      {/* All three slots, always (parity P-SDASH-04): `seo.html#dash` draws
          LCP / INP / CLS as one row, and a metric this run could not measure is
          a dash with the reason on its badge rather than a paragraph under two
          figures. */}
      <div className="app-cwv">
        {VITALS.map((vital) => {
          const reading = measured.metrics.find((row) => row.metric === vital.metric)
          return reading === undefined ? (
            <Unmeasured key={vital.metric} vital={vital} measured={measured} />
          ) : (
            <Vital key={vital.metric} reading={reading} />
          )
        })}
      </div>
    </div>
  )
}

/** The three Core Web Vitals, in the order the card draws them. */
const VITALS: readonly { metric: string; label: string }[] = [
  { metric: 'lcp', label: 'LCP' },
  { metric: 'inp', label: 'INP' },
  { metric: 'cls', label: 'CLS' },
]

/**
 * A slot for a metric this measurement does not carry, and why.
 *
 * A lab run has no INP — it measures a human tapping things, and a lab taps
 * nothing (`app/seo/pagespeed.py`) — so a property with too little traffic for
 * Chrome's field data shows two figures, and sometimes one when Lighthouse could
 * not settle an LCP for the page. The slot stays in the row so the three read
 * as the three; the reason is on the badge, where a pointer or a screen reader
 * finds it. An absence with a reason is a fact; an absence without one looks
 * like a fault.
 */
function Unmeasured({
  vital,
  measured,
}: {
  vital: { metric: string; label: string }
  measured: VitalsMeasurement
}): JSX.Element {
  const why =
    vital.metric === 'inp'
      ? measured.field_data
        ? 'INP was not reported for this property in this period.'
        : 'INP needs real Chrome users — a lab run cannot measure it, and this property does not have enough traffic for Google to report it yet.'
      : `${vital.label} was not reported by this ${measured.field_data ? 'period' : 'run'}; the next audit measures again.`
  return (
    <div>
      <div className="ar-muted" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
        {vital.label}
      </div>
      <div className="ar-h2 ar-num ar-subtle">—</div>
      <span className="ar-badge ar-badge--neutral" title={why}>
        Not measured
      </span>
    </div>
  )
}

function Vital({ reading }: { reading: VitalsReading }): JSX.Element {
  const tone =
    reading.band === 'good'
      ? 'ar-badge--success'
      : reading.band === 'poor'
        ? 'ar-badge--danger'
        : 'ar-badge--warning'
  const verdict =
    reading.band === 'good' ? '✓ Good' : reading.band === 'poor' ? '✗ Poor' : '⚠ Needs work'

  return (
    <div>
      <div className="ar-muted" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
        {reading.label}
      </div>
      <div className={`ar-h2 ar-num app-cwv__value--${reading.band}`}>{reading.display}</div>
      <span className={`ar-badge ${tone}`}>{verdict}</span>
    </div>
  )
}

type SearchQuery = ReturnType<typeof useQuery<SearchDashboard | null>>
type ContentQuery = ReturnType<typeof useQuery<ContentDashboard | null>>

/**
 * The four headline tiles.
 *
 * Three come from Google and one from us, which is why a Search Console failure
 * dims three of them and leaves the fourth standing rather than emptying the row.
 */
function Tiles({
  search,
  content,
  hasProperty,
  days,
}: {
  search: SearchQuery
  content: ContentQuery
  hasProperty: boolean
  days: Range
}): JSX.Element {
  if (!hasProperty) {
    return (
      <div className="ar-card">
        <Empty
          icon="🔍"
          title="Connect Search Console to see how this site is doing"
          body="Clicks, impressions and average position come from Google. Choose which Search Console property this site is and the figures appear here."
          action={
            <Link
              className="ar-btn ar-btn--primary"
              to="/settings/connections#search-console-properties"
            >
              Choose a property
            </Link>
          }
        />
      </div>
    )
  }

  if (search.state.status === 'loading' || content.state.status === 'loading') {
    return <Loading label="Loading your figures" lines={4} />
  }

  if (search.state.status === 'error') {
    return (
      <div className="ar-card">
        <ErrorState
          failure={search.state.failure}
          title="Could not load your Search Console figures"
          onRetry={search.refetch}
        />
        {/* The content tile is still good, and saying so is the point of the
            two-route split. */}
        {content.state.status === 'ready' && content.state.data !== null ? (
          <div className="ar-card__body">
            <PublishedTile data={content.state.data} />
          </div>
        ) : null}
      </div>
    )
  }

  // `search` is narrowed to ready by the two returns above. `content` is not —
  // only its loading state was excluded — so a content failure still leaves the
  // three Search Console tiles standing, which is the split's whole purpose.
  const figures = search.state.data
  const published = content.state.status === 'ready' ? content.state.data : null

  if (figures !== null && !figures.has_data) {
    return (
      <div className="ar-card">
        <Empty
          icon="🔍"
          title="Google has no data for this property yet"
          body={`Search Console reports two to three days behind, and a property registered recently has nothing to report at all. Figures for the last ${String(days)} days will appear here once Google has them.`}
        />
      </div>
    )
  }

  return (
    <div className="ar-grid ar-grid--4">
      {figures !== null ? (
        <>
          <Tile
            label="Average position"
            moved={figures.position}
            comparable={figures.comparable}
            unit="places"
          />
          <Tile label="Clicks" moved={figures.clicks} comparable={figures.comparable} />
          <Tile label="Impressions" moved={figures.impressions} comparable={figures.comparable} />
        </>
      ) : null}
      {published !== null ? (
        <div className="ar-card">
          <div className="ar-card__body">
            <PublishedTile data={published} />
          </div>
        </div>
      ) : null}
    </div>
  )
}

/**
 * One Search Console tile.
 *
 * `current` of `null` renders an em dash rather than a zero: an unmeasured
 * window has no average position, and rank 0 does not exist.
 */
function Tile({
  label,
  moved,
  comparable,
  unit = 'percent',
}: {
  label: string
  moved: Tile
  comparable: boolean
  /** SEO-UI-22. `places` on the rank tile, `percent` on the counting ones. */
  unit?: 'percent' | 'places'
}): JSX.Element {
  return (
    <div className="ar-card">
      <div className="ar-card__body">
        <div className="ar-muted" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
          {label}
        </div>
        <div className="ar-h1 ar-num">
          {moved.current === null ? '—' : moved.current.toLocaleString('en-IN')}
        </div>
        {comparable ? <Badge moved={moved} unit={unit} /> : null}
      </div>
    </div>
  )
}

/**
 * The change badge.
 *
 * **Colour comes from `direction`.** `flat` gets a neutral badge rather than no
 * badge — *"unchanged"* is a measurement and hiding it would make a steady month
 * look like a month with no comparison, which is a different thing entirely.
 *
 * A `null` percent with a real delta is the rise-from-nothing case, and it reads
 * as *new* rather than as a percentage that does not exist.
 *
 * **SEO-UI-22 — a rank moves by places, never by a percentage.** Every badge on
 * this screen used to render `percent`, and on the *Average position* tile that
 * is a number with no meaning: going from 9.7 to 5.5 is not "43% better" of
 * anything, because position is an ordinal and the ratio of two ranks is not a
 * quantity. `seo.html:108` draws that tile as *"▲ 4.2 vs last month"* — the raw
 * delta — and the three counting tiles beside it as percentages, which is the
 * distinction `unit` carries. It is the call site's because only the call site
 * knows what it is counting; `Movement` deliberately carries both figures so
 * neither has to be re-derived from the other.
 */
function Badge({
  moved,
  unit = 'percent',
}: {
  moved: Tile
  unit?: 'percent' | 'places'
}): JSX.Element | null {
  if (moved.direction === 'unknown' || moved.delta === null) return null

  const tone =
    moved.direction === 'better'
      ? 'ar-badge--success'
      : moved.direction === 'worse'
        ? 'ar-badge--danger'
        : 'ar-badge--neutral'
  const arrow = moved.direction === 'better' ? '▲' : moved.direction === 'worse' ? '▼' : '■'
  const places = Math.abs(moved.delta)
  const magnitude =
    unit === 'places'
      ? `${places.toFixed(1)} place${places === 1 ? '' : 's'}`
      : moved.percent === null
        ? `${Math.abs(moved.delta).toLocaleString('en-IN')} new`
        : `${Math.abs(moved.percent).toFixed(1)}%`

  return (
    <div className={`ar-badge ${tone}`} style={{ marginTop: 'var(--ar-space-2)' }}>
      {moved.direction === 'flat' ? 'Unchanged' : `${arrow} ${magnitude} vs previous period`}
    </div>
  )
}

/**
 * The one tile AugRank answers for itself.
 *
 * Its window ends **now** where the Search Console tiles end three days back, so
 * the caption says which period it is about rather than borrowing the row's.
 */
function PublishedTile({ data }: { data: ContentDashboard }): JSX.Element {
  return (
    <>
      <div className="ar-muted" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
        Published
      </div>
      <div className="ar-h1 ar-num">{data.published.toLocaleString('en-IN')}</div>
      {data.awaiting_approval > 0 ? (
        <Link
          className="ar-badge ar-badge--warning"
          style={{ marginTop: 'var(--ar-space-2)' }}
          to="/seo/approvals"
        >
          ⚠ {data.awaiting_approval} awaiting you
        </Link>
      ) : (
        <div
          className="ar-muted"
          style={{ marginTop: 'var(--ar-space-2)', fontSize: 'var(--ar-text-body-sm-size)' }}
        >
          Last {data.days} days
        </div>
      )}
    </>
  )
}

/**
 * Rank movement over the window.
 *
 * **Drawn with the axis inverted**, because position 1 is the top of the page:
 * a line that rose as the number rose would show every improvement as a fall.
 * The y-axis therefore runs from the best position at the top to the worst at
 * the bottom, which is what Search Console's own chart does.
 *
 * **Gaps are gaps.** A day Google has not reported is absent from `points` and
 * is not interpolated here either — the polyline simply skips it. Joining across
 * it would draw a straight line through days nobody measured.
 *
 * The data table is the accessible equivalent of the chart rather than an extra:
 * the `<svg>` is `aria-hidden` and the table carries the same numbers, so the
 * screen has one source of truth in two renderings.
 */
function RankMovement({ search }: { search: SearchQuery }): JSX.Element | null {
  const [showTable, setShowTable] = useState(false)

  if (search.state.status !== 'ready' || search.state.data === null) return null
  const data = search.state.data
  const points = data.points
  // Per keyword once a rank run has written positions (parity P-SDASH-09);
  // the site's average position, as before, until then.
  const perKeyword = hasKeywordLines(data)
  if (points.length === 0 && !perKeyword) return null

  return (
    <div className="ar-card">
      <div className="ar-card__header">
        <h2>Rank movement</h2>
        <span className="ar-card__spacer" />
        {/* The queries behind these figures. Search performance left the rail
            (SHELL-13) and, since batch 2 of the settings consolidation moved
            its pickers to Connections, this is its entry point. */}
        <Link className="ar-btn ar-btn--ghost ar-btn--sm" to="/seo/search-performance">
          Top queries →
        </Link>
        <button
          type="button"
          className="ar-btn ar-btn--ghost ar-btn--sm"
          aria-expanded={showTable}
          onClick={() => {
            setShowTable((shown) => !shown)
          }}
        >
          {showTable ? 'Hide data table' : 'Show data table'}
        </button>
      </div>
      <div className="ar-card__body">
        {/* One reported day is a mark, not a line: a polyline with one point
            draws nothing, and the card was a blank 280px box (walkthrough
            2026-09-12). The sentence says why there is no line yet. */}
        {perKeyword ? (
          <>
            <KeywordRankChart data={data} />
            {showTable ? <KeywordRankTable data={data} /> : null}
          </>
        ) : (
          <>
            {points.length === 1 ? (
              <p className="ar-muted ar-text-body-sm app-rank-chart__note">
                One day reported so far — average position{' '}
                {(points[0] as Point).position.toFixed(1)} on {points[0]?.on}. The line appears from
                the second day Google reports.
              </p>
            ) : null}
            <Chart points={points} />
          </>
        )}
        {/* The site's own days — clicks and impressions as well as position —
            stay behind the same button whichever chart is drawn. */}
        {showTable && points.length > 0 ? <DataTable points={points} /> : null}
      </div>
    </div>
  )
}

/**
 * The polyline, on an inverted y-axis. Decorative — the table is the content.
 *
 * The best and worst positions are written at the two ends of the axis, so a
 * line has a scale: without them a two-point chart from 9.9 to 9.4 filled the
 * whole box and read as a collapse (walkthrough 2026-09-12). A single point is
 * drawn as a circle, since a one-point polyline has no length.
 */
function Chart({ points }: { points: Point[] }): JSX.Element {
  const width = 720
  const height = 240
  const positions = points.map((point) => point.position)
  const best = Math.min(...positions)
  const worst = Math.max(...positions)
  // A flat line sits in the middle rather than dividing by a zero range.
  const span = worst - best || 1
  const step = points.length > 1 ? width / (points.length - 1) : 0

  const coords = points.map((point, index) => {
    const x = points.length > 1 ? index * step : width / 2
    // Inverted: the best position is at the top of the box.
    const y = span === 1 && worst === best ? height / 2 : ((point.position - best) / span) * height
    return { x: Math.round(x), y: Math.round(y) }
  })
  const path = coords.map((c) => `${String(c.x)},${String(c.y)}`).join(' ')

  return (
    <div className="app-rank-chart__frame">
      <div className="app-rank-chart__axis" aria-hidden="true">
        <span>#{best.toFixed(1)}</span>
        <span>#{worst.toFixed(1)}</span>
      </div>
      <svg
        className="app-rank-chart"
        viewBox={`0 0 ${String(width)} ${String(height)}`}
        preserveAspectRatio="none"
        aria-hidden="true"
        focusable="false"
      >
        {coords.length > 1 ? (
          <polyline
            className="app-rank-chart__line"
            points={path}
            vectorEffect="non-scaling-stroke"
          />
        ) : (
          coords.map((c) => (
            <circle
              key={`${String(c.x)}-${String(c.y)}`}
              className="app-rank-chart__point"
              cx={c.x}
              cy={c.y}
              r={6}
              vectorEffect="non-scaling-stroke"
            />
          ))
        )}
      </svg>
      <div className="app-rank-chart__axis app-rank-chart__axis--x" aria-hidden="true">
        <span>{points[0]?.on}</span>
        <span>{points[points.length - 1]?.on}</span>
      </div>
    </div>
  )
}

function DataTable({ points }: { points: Point[] }): JSX.Element {
  return (
    <table className="ar-table" style={{ marginTop: 'var(--ar-space-5)' }}>
      <caption className="ar-muted">
        Average position, clicks and impressions per day, as Search Console reported them.
      </caption>
      <thead>
        <tr>
          <th scope="col">Day</th>
          <th scope="col">Position</th>
          <th scope="col">Clicks</th>
          <th scope="col">Impressions</th>
        </tr>
      </thead>
      <tbody>
        {points.map((point) => (
          <tr key={point.on}>
            <td>{point.on}</td>
            <td className="ar-num">{point.position.toFixed(1)}</td>
            <td className="ar-num">{point.clicks.toLocaleString('en-IN')}</td>
            <td className="ar-num">{point.impressions.toLocaleString('en-IN')}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
